import { useUnits } from './useUnits';
import { setDisplayLocale } from '../utils/format';

/**
 * Point the shared date formatters at the tenant's resolved locale.
 *
 * ⚠️ **Set DURING render, not in an effect.** An effect runs after the first paint, so the
 * page would render once in the previous locale and then again in the new one — a visible
 * flip of every date on screen, and on a slow page a reader could act on the first one.
 * Assigning a module value while rendering is normally a smell; here the value is a display
 * preference read by pure formatters, it is idempotent, and it must be correct BEFORE the
 * children render rather than after.
 *
 * ⚠️ **It returns the locale so the caller can KEY on it.** The formatters are plain
 * functions, so a component that calls `absolute()` and nothing else has no subscription
 * and would keep its old text when the preference changes. Keying the subtree on the
 * locale remounts it — heavy for a normal state change and exactly right for this one,
 * which happens when somebody saves a setting and never otherwise. Without it a save
 * updates the settings page and leaves every other open page reading the old convention,
 * which is the two-surfaces-disagree fault this codebase keeps paying for.
 *
 * ⚠️ No query of its own: `useUnits` already fetches this, cached for an hour and
 * invalidated by the settings page on save. A second request for a value the app has in
 * hand is the `PartnerHierarchyTree` lesson — mounting IS the fetch.
 */
export function useDisplayLocale(): string {
  const { locale } = useUnits();
  setDisplayLocale(locale);
  return locale;
}
